import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SubmitButton from './SubmitButton';

/**
 * ConfirmDialog - the one confirmation (design.md §5.2, §5.4).
 *
 * Replaces the hand-rolled confirms across the admin, agent and seller areas,
 * each of which had picked its own button order, variants and close affordance.
 * Cancel sits on the left as a text button, the confirming action on the right;
 * a destructive action is error-coloured and never pre-focused, so Enter does
 * not delete anything by accident.
 *
 * The theme makes dialogs edge-to-edge under `sm`; `fullWidth` keeps them a
 * predictable width above it.
 *
 * Props:
 *  - open, onClose   : standard dialog control
 *  - onConfirm       : called when the confirming action is pressed
 *  - title           : short question, e.g. "Delete this listing?"
 *  - description     : what will happen; omit if the title says it all
 *  - children        : extra content rendered under the description
 *  - confirmLabel    : defaults to 'Confirm'; start it with a verb
 *  - confirmIcon     : optional icon before the confirming label, to match
 *                      the button the dialog stands in for
 *  - cancelLabel     : defaults to 'Cancel'
 *  - destructive     : colours the confirming action as an error
 *  - pending         : disables both actions and spins the confirming one
 *  - confirmDisabled : disables the confirming action, e.g. until a typed
 *                      confirmation matches
 *  - maxWidth        : defaults to 'xs'
 */
const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  children,
  confirmLabel = 'Confirm',
  confirmIcon,
  cancelLabel = 'Cancel',
  destructive = false,
  pending = false,
  confirmDisabled = false,
  maxWidth = 'xs',
  ...dialogProps
}) => (
  <Dialog
    open={Boolean(open)}
    onClose={pending ? undefined : onClose}
    fullWidth
    maxWidth={maxWidth}
    aria-labelledby="confirm-dialog-title"
    {...dialogProps}
  >
    <DialogTitle id="confirm-dialog-title" sx={{ pr: 6 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <span>{title}</span>
        {onClose && (
          <IconButton
            aria-label="Close"
            onClick={onClose}
            disabled={pending}
            size="small"
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
    </DialogTitle>

    <DialogContent>
      {description && <DialogContentText>{description}</DialogContentText>}
      {children}
    </DialogContent>

    <DialogActions sx={{ px: { xs: 1.5, sm: 3 }, pb: { xs: 1.5, sm: 2 } }}>
      <Button onClick={onClose} disabled={pending} color="inherit">
        {cancelLabel}
      </Button>
      <SubmitButton
        type="button"
        onClick={onConfirm}
        pending={pending}
        disabled={confirmDisabled}
        startIcon={confirmIcon}
        color={destructive ? 'error' : 'primary'}
        variant="contained"
      >
        {confirmLabel}
      </SubmitButton>
    </DialogActions>
  </Dialog>
);

export default ConfirmDialog;
